/* eslint-disable import/no-extraneous-dependencies */
import { closestCorners, DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WalToast from 'src/utils/WalToast';

import DraggableColumn from './DraggableColumn';
import { IDraggableLayout } from './draggableLayout.interface';

const DraggableLayout: FC<IDraggableLayout> = ({ sortableData, renderCellContent: CustomRenderCellContent, onColumnChange }) => {
    const { t } = useTranslation();
    const [columns, setColumns] = useState<any[]>([]);

    useEffect(() => {
        if (sortableData && sortableData.length > 0) {
            setColumns(
                sortableData[0].map((col: any, index: number) => ({
                    visible: col?.visible ?? true,
                    displayOrder: index + 1,
                    ...col,
                }))
            );
        }
    }, [sortableData]);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setColumns((items) => {
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                const reorderedItems = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
                    ...item,
                    displayOrder: index + 1,
                }));
                onColumnChange(reorderedItems);

                return reorderedItems;
            });
        }
    };

    const handleColumnVisibilityToggle = useCallback(
        (columnId: string) => {
            setColumns((items) => {
                const columnToToggle = items.find((item) => item.id === columnId);

                if (columnToToggle?.visible !== false) {
                    const visibleColumnsCount = items.filter((item) => item.visible !== false).length;

                    if (visibleColumnsCount === 1) {
                        WalToast.error(t('QuotingHub.QuotingDashboard.Keep_at_least_one_column'));

                        return items;
                    }
                }

                const updatedColumns = items.map((item) => (item.id === columnId ? { ...item, visible: !item.visible } : item));
                onColumnChange(updatedColumns);

                return updatedColumns;
            });
        },
        [onColumnChange]
    );

    if (!columns.length) {
        return <div className="p-4 text-gray-500">No data available</div>;
    }

    const ActiveCellRenderer = CustomRenderCellContent;

    return (
        <div className="flex overflow-x-auto custom-scrollbar-horizontal gap-3 px-3 py-2">
            <DndContext collisionDetection={closestCorners} onDragEnd={handleDragEnd} modifiers={[restrictToHorizontalAxis, restrictToParentElement]}>
                <SortableContext items={columns.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
                    {columns.map((column) => (
                        <DraggableColumn key={column.id} column={column} sortableData={sortableData} renderCellContent={ActiveCellRenderer} onColumnVisibilityToggle={handleColumnVisibilityToggle} />
                    ))}
                </SortableContext>
            </DndContext>
        </div>
    );
};

export default DraggableLayout;
